export type ErrorCategory =
  | 'resume_unreadable'   // RESUME_IMAGE_ONLY, RESUME_EMPTY, RESUME_PARSE_FAILED
  | 'resume_format'       // RESUME_FORMAT_COMPLEX
  | 'resume_language'     // RESUME_LANGUAGE_UNSUPPORTED
  | 'ai_unavailable'      // AI_SERVICE_UNAVAILABLE, JD_GENERATION_FAILED, HTTP 500/503
  | 'match_failed'        // PROFILE_MATCH_FAILED (soft-error: status:false on HTTP 200)
  | 'access_denied'       // 403
  | 'session_expired'     // 401
  | 'rate_limited'        // 429
  | 'network_failure'     // ECONNABORTED, no response
  | 'invalid_zip'         // INVALID_ZIP_FORMAT — corrupt/empty ZIP file
  | 's3_upload_failed'    // S3_UPLOAD_FAILED, PRESIGNED_URL_EXPIRED
  | 'processing_timeout'  // PROCESSING_TIMEOUT — bulk processing took too long
  | 'unknown'

export interface ClassifiedError {
  category: ErrorCategory
  isRetryable: boolean
  requiresUserAction: boolean
  httpStatus?: number
  rawMessage?: string   // pass-through for logging/debug, never shown to user
}

export function classifyError(error: any): ClassifiedError {
  const status = error?.response?.status
  const data = error?.response?.data
  const errorCode = data?.errorCode
  const message = error?.message || ''

  // Priority 1: Check errorCode from backend
  if (errorCode) {
    if (['RESUME_IMAGE_ONLY', 'RESUME_EMPTY', 'RESUME_PARSE_FAILED'].includes(errorCode)) {
      return {
        category: 'resume_unreadable',
        isRetryable: false,
        requiresUserAction: true,
        httpStatus: status,
        rawMessage: data?.message,
      }
    }
    if (errorCode === 'RESUME_FORMAT_COMPLEX') {
      return {
        category: 'resume_format',
        isRetryable: false,
        requiresUserAction: true,
        httpStatus: status,
        rawMessage: data?.message,
      }
    }
    if (errorCode === 'RESUME_LANGUAGE_UNSUPPORTED') {
      return {
        category: 'resume_language',
        isRetryable: false,
        requiresUserAction: true,
        httpStatus: status,
        rawMessage: data?.message,
      }
    }
    if (['AI_SERVICE_UNAVAILABLE', 'JD_GENERATION_FAILED'].includes(errorCode)) {
      return {
        category: 'ai_unavailable',
        isRetryable: true,
        requiresUserAction: false,
        httpStatus: status,
        rawMessage: data?.message,
      }
    }
    if (errorCode === 'PROFILE_MATCH_FAILED') {
      return {
        category: 'match_failed',
        isRetryable: true,
        requiresUserAction: false,
        httpStatus: status,
        rawMessage: data?.message,
      }
    }
    if (errorCode === 'INVALID_ZIP_FORMAT') {
      return {
        category: 'invalid_zip',
        isRetryable: false,
        requiresUserAction: true,
        httpStatus: status,
        rawMessage: data?.message,
      }
    }
    if (['S3_UPLOAD_FAILED', 'PRESIGNED_URL_EXPIRED'].includes(errorCode)) {
      return {
        category: 's3_upload_failed',
        isRetryable: true,
        requiresUserAction: false,
        httpStatus: status,
        rawMessage: data?.message,
      }
    }
    if (errorCode === 'PROCESSING_TIMEOUT') {
      return {
        category: 'processing_timeout',
        isRetryable: true,
        requiresUserAction: false,
        httpStatus: status,
        rawMessage: data?.message,
      }
    }
  }

  // Priority 2: Check HTTP status codes
  if (status === 401) {
    return {
      category: 'session_expired',
      isRetryable: false,
      requiresUserAction: false,
      httpStatus: 401,
      rawMessage: data?.message,
    }
  }

  if (status === 403) {
    return {
      category: 'access_denied',
      isRetryable: false,
      requiresUserAction: false,
      httpStatus: 403,
      rawMessage: data?.message,
    }
  }

  if (status === 400) {
    // 400 with INVALID_ZIP_FORMAT (corrupt or empty ZIP)
    if (errorCode === 'INVALID_ZIP_FORMAT') {
      return {
        category: 'invalid_zip',
        isRetryable: false,
        requiresUserAction: true,
        httpStatus: 400,
        rawMessage: data?.message,
      }
    }
  }

  if (status === 410) {
    // 410 GONE — Presigned URL expired
    return {
      category: 's3_upload_failed',
      isRetryable: true,
      requiresUserAction: false,
      httpStatus: 410,
      rawMessage: data?.message,
    }
  }

  if (status === 422) {
    // Resume content failure without specific errorCode — default to unreadable
    return {
      category: 'resume_unreadable',
      isRetryable: false,
      requiresUserAction: true,
      httpStatus: 422,
      rawMessage: data?.message,
    }
  }

  if (status === 429) {
    return {
      category: 'rate_limited',
      isRetryable: true,
      requiresUserAction: false,
      httpStatus: 429,
      rawMessage: data?.message,
    }
  }

  if (status === 504) {
    // 504 Gateway Timeout — bulk processing timeout
    return {
      category: 'processing_timeout',
      isRetryable: true,
      requiresUserAction: false,
      httpStatus: 504,
      rawMessage: data?.message,
    }
  }

  if (status === 500 || status === 503) {
    return {
      category: 'ai_unavailable',
      isRetryable: true,
      requiresUserAction: false,
      httpStatus: status,
      rawMessage: data?.message,
    }
  }

  // Priority 3: Check for network errors (no response)
  if (!error?.response) {
    const isNetworkError =
      error?.code === 'ECONNABORTED' ||
      message.includes('timeout') ||
      message.includes('Network Error') ||
      message.includes('ERR_NETWORK')

    if (isNetworkError) {
      return {
        category: 'network_failure',
        isRetryable: true,
        requiresUserAction: false,
        rawMessage: message,
      }
    }
  }

  // Priority 4: Fallback to unknown
  return {
    category: 'unknown',
    isRetryable: true,
    requiresUserAction: false,
    httpStatus: status,
    rawMessage: message,
  }
}
